<template>
  <v-container fluid>
    <v-layout row wrap>

      <v-flex xs12>
        <skeleton-chart v-if="loading" :bar-count="10" show-legend></skeleton-chart>
        <v-card v-else class="elevation-1">
          <v-card-title class="pa-2">
            <div>
              <h4 class="font-weight-regular">Total Volume Over Time</h4>
              <div class="caption grey--text">Monthly Combined Transaction Trends</div>
            </div>
          </v-card-title>
          <v-card-text class="pa-2">
            <v-chart :option="totalChartOptions" :loading="isLoading" :loading-options="{ text: 'Loading chart...', color: '#1976D2' }" style="height: 450px;" autoresize />
          </v-card-text>
          <v-card-text class="caption grey--text pa-2 pt-0">
            Displaying {{ displayedMerchantCount }} of {{ totalMerchantCount }} merchants
          </v-card-text>
        </v-card>
      </v-flex>

      <v-flex xs12>
        <skeleton-chart v-if="loading" :bar-count="10" show-legend></skeleton-chart>
        <v-card v-else class="elevation-1">
          <v-card-title class="pa-2">
            <div>
              <h4 class="font-weight-regular">Card Volume Over Time</h4>
              <div class="caption grey--text">Monthly Card Transaction Trends</div>
            </div>
          </v-card-title>
          <v-card-text class="pa-2">
            <v-chart :option="cardChartOptions" :loading="isLoading" :loading-options="{ text: 'Loading chart...', color: '#1976D2' }" style="height: 450px;" autoresize />
          </v-card-text>
          <v-card-text class="caption grey--text pa-2 pt-0">
            Displaying {{ displayedMerchantCount }} of {{ totalMerchantCount }} merchants
          </v-card-text>
        </v-card>
      </v-flex>

      <v-flex xs12>
        <skeleton-chart v-if="loading" :bar-count="10" show-legend></skeleton-chart>
        <v-card v-else class="elevation-1">
          <v-card-title class="pa-2">
            <div>
              <h4 class="font-weight-regular">ACH Volume Over Time</h4>
              <div class="caption grey--text">Monthly ACH Transaction Trends</div>
            </div>
          </v-card-title>
          <v-card-text class="pa-2">
            <v-chart :option="achChartOptions" :loading="isLoading" :loading-options="{ text: 'Loading chart...', color: '#1976D2' }" style="height: 450px;" autoresize />
          </v-card-text>
          <v-card-text class="caption grey--text pa-2 pt-0">
            Displaying {{ displayedMerchantCount }} of {{ totalMerchantCount }} merchants
          </v-card-text>
        </v-card>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import VChart from 'vue-echarts'
import FundingApi from '@/mixins/fundingApi'
import SkeletonChart from '@/components/common/SkeletonChart'

export default {
  components: {
    VChart,
    SkeletonChart
  },

  mixins: [FundingApi],

  props: {
    merchants: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    startDate: {
      type: Date,
      required: true
    },
    endDate: {
      type: Date,
      required: true
    },
    selectedMerchantKsuids: {
      type: Array,
      default: () => []
    }
  },

  data () {
    return {
      topMerchantCount: 10,
      trendsData: {
        dates: [],
        series: []
      },
      internalLoading: false,
      fetchDebounceTimer: null
    }
  },

  computed: {
    isLoading () {
      return this.loading || this.internalLoading
    },

    displayedMerchantCount () {
      if (this.selectedMerchantKsuids.length === 0) {
        return this.trendsData.series.length
      }
      return this.selectedMerchantKsuids.length
    },

    totalMerchantCount () {
      return this.merchants.length
    },

    totalChartOptions () {
      if (this.selectedMerchantKsuids.length === 0 && this.trendsData.series.length > 0) {
        const allData = this.trendsData.dates.map((date, idx) => {
          return this.trendsData.series.reduce((sum, s) => sum + (s.total_data[idx] || 0), 0)
        })

        const series = [{
          name: 'All Merchants',
          type: 'line',
          smooth: true,
          stack: 'total',
          areaStyle: {},
          data: allData,
          itemStyle: {
            color: this.getColorForIndex(0)
          }
        }]

        return {
          tooltip: {
            trigger: 'axis',
            axisPointer: {
              type: 'cross'
            },
            formatter: (params) => {
              let result = `${params[0].axisValue}<br/>`
              result += `${params[0].marker} ${params[0].seriesName}: ${this.formatCurrency(params[0].value)}`
              return result
            }
          },
          toolbox: {
            feature: {
              saveAsImage: { title: 'Export PNG' },
              dataZoom: { title: { zoom: 'Zoom', back: 'Reset' } },
              restore: { title: 'Restore' }
            }
          },
          legend: {
            data: ['All Merchants'],
            bottom: 0,
            type: 'scroll'
          },
          dataZoom: [
            { type: 'inside' },
            { type: 'slider', height: 20, bottom: 60 }
          ],
          grid: {
            left: '3%',
            right: '4%',
            bottom: '20%',
            top: '10%',
            containLabel: true
          },
          xAxis: {
            type: 'category',
            boundaryGap: false,
            data: this.trendsData.dates,
            axisLabel: {
              rotate: 45,
              formatter: (value) => {
                const date = new Date(value)
                return `${date.getMonth() + 1}/${date.getDate()}`
              }
            }
          },
          yAxis: {
            type: 'value',
            axisLabel: {
              formatter: (value) => this.formatAxisCurrency(value)
            }
          },
          series: series
        }
      }

      const series = this.trendsData.series.map((merchantData, index) => ({
        name: merchantData.merchant_name,
        type: 'line',
        smooth: true,
        stack: 'total',
        areaStyle: {},
        data: merchantData.total_data,
        itemStyle: {
          color: this.getColorForIndex(index)
        }
      }))

      return {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'cross'
          },
          formatter: (params) => {
            let result = `${params[0].axisValue}<br/>`
            let total = 0
            params.reverse().forEach(param => {
              result += `${param.marker} ${param.seriesName}: ${this.formatCurrency(param.value)}<br/>`
              total += param.value
            })
            result += `<strong>Total: ${this.formatCurrency(total)}</strong>`
            return result
          }
        },
        toolbox: {
          feature: {
            saveAsImage: { title: 'Export PNG' },
            dataZoom: { title: { zoom: 'Zoom', back: 'Reset' } },
            restore: { title: 'Restore' }
          }
        },
        legend: {
          data: this.trendsData.series.map(s => s.merchant_name),
          bottom: 0,
          type: 'scroll'
        },
        dataZoom: [
          { type: 'inside' },
          { type: 'slider', height: 20, bottom: 60 }
        ],
        grid: {
          left: '3%',
          right: '4%',
          bottom: '20%',
          top: '10%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: this.trendsData.dates,
          axisLabel: {
            rotate: 45,
            formatter: (value) => {
              const date = new Date(value)
              return `${date.getMonth() + 1}/${date.getDate()}`
            }
          }
        },
        yAxis: {
          type: 'value',
          axisLabel: {
            formatter: (value) => this.formatAxisCurrency(value)
          }
        },
        series: series
      }
    },

    cardChartOptions () {
      if (this.selectedMerchantKsuids.length === 0 && this.trendsData.series.length > 0) {
        const allData = this.trendsData.dates.map((date, idx) => {
          return this.trendsData.series.reduce((sum, s) => sum + (s.card_data[idx] || 0), 0)
        })

        const series = [{
          name: 'All Merchants',
          type: 'line',
          smooth: true,
          stack: 'total',
          areaStyle: {},
          data: allData,
          itemStyle: {
            color: this.getColorForIndex(0)
          }
        }]

        return {
          tooltip: {
            trigger: 'axis',
            axisPointer: {
              type: 'cross'
            },
            formatter: (params) => {
              let result = `${params[0].axisValue}<br/>`
              result += `${params[0].marker} ${params[0].seriesName}: ${this.formatCurrency(params[0].value)}`
              return result
            }
          },
          toolbox: {
            feature: {
              saveAsImage: { title: 'Export PNG' },
              dataZoom: { title: { zoom: 'Zoom', back: 'Reset' } },
              restore: { title: 'Restore' }
            }
          },
          legend: {
            data: ['All Merchants'],
            bottom: 0,
            type: 'scroll'
          },
          dataZoom: [
            { type: 'inside' },
            { type: 'slider', height: 20, bottom: 60 }
          ],
          grid: {
            left: '3%',
            right: '4%',
            bottom: '20%',
            top: '10%',
            containLabel: true
          },
          xAxis: {
            type: 'category',
            boundaryGap: false,
            data: this.trendsData.dates,
            axisLabel: {
              rotate: 45,
              formatter: (value) => {
                const date = new Date(value)
                return `${date.getMonth() + 1}/${date.getDate()}`
              }
            }
          },
          yAxis: {
            type: 'value',
            axisLabel: {
              formatter: (value) => this.formatAxisCurrency(value)
            }
          },
          series: series
        }
      }

      const series = this.trendsData.series.map((merchantData, index) => ({
        name: merchantData.merchant_name,
        type: 'line',
        smooth: true,
        stack: 'total',
        areaStyle: {},
        data: merchantData.card_data,
        itemStyle: {
          color: this.getColorForIndex(index)
        }
      }))

      return {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'cross'
          },
          formatter: (params) => {
            let result = `${params[0].axisValue}<br/>`
            let total = 0
            params.reverse().forEach(param => {
              result += `${param.marker} ${param.seriesName}: ${this.formatCurrency(param.value)}<br/>`
              total += param.value
            })
            result += `<strong>Total: ${this.formatCurrency(total)}</strong>`
            return result
          }
        },
        toolbox: {
          feature: {
            saveAsImage: { title: 'Export PNG' },
            dataZoom: { title: { zoom: 'Zoom', back: 'Reset' } },
            restore: { title: 'Restore' }
          }
        },
        legend: {
          data: this.trendsData.series.map(s => s.merchant_name),
          bottom: 0,
          type: 'scroll'
        },
        dataZoom: [
          { type: 'inside' },
          { type: 'slider', height: 20, bottom: 60 }
        ],
        grid: {
          left: '3%',
          right: '4%',
          bottom: '20%',
          top: '10%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: this.trendsData.dates,
          axisLabel: {
            rotate: 45,
            formatter: (value) => {
              const date = new Date(value)
              return `${date.getMonth() + 1}/${date.getDate()}`
            }
          }
        },
        yAxis: {
          type: 'value',
          axisLabel: {
            formatter: (value) => this.formatAxisCurrency(value)
          }
        },
        series: series
      }
    },

    achChartOptions () {
      if (this.selectedMerchantKsuids.length === 0 && this.trendsData.series.length > 0) {
        const allData = this.trendsData.dates.map((date, idx) => {
          return this.trendsData.series.reduce((sum, s) => sum + (s.bank_data[idx] || 0), 0)
        })

        const series = [{
          name: 'All Merchants',
          type: 'line',
          smooth: true,
          stack: 'total',
          areaStyle: {},
          data: allData,
          itemStyle: {
            color: this.getColorForIndex(0)
          }
        }]

        return {
          tooltip: {
            trigger: 'axis',
            axisPointer: {
              type: 'cross'
            },
            formatter: (params) => {
              let result = `${params[0].axisValue}<br/>`
              result += `${params[0].marker} ${params[0].seriesName}: ${this.formatCurrency(params[0].value)}`
              return result
            }
          },
          toolbox: {
            feature: {
              saveAsImage: { title: 'Export PNG' },
              dataZoom: { title: { zoom: 'Zoom', back: 'Reset' } },
              restore: { title: 'Restore' }
            }
          },
          legend: {
            data: ['All Merchants'],
            bottom: 0,
            type: 'scroll'
          },
          dataZoom: [
            { type: 'inside' },
            { type: 'slider', height: 20, bottom: 60 }
          ],
          grid: {
            left: '3%',
            right: '4%',
            bottom: '20%',
            top: '10%',
            containLabel: true
          },
          xAxis: {
            type: 'category',
            boundaryGap: false,
            data: this.trendsData.dates,
            axisLabel: {
              rotate: 45,
              formatter: (value) => {
                const date = new Date(value)
                return `${date.getMonth() + 1}/${date.getDate()}`
              }
            }
          },
          yAxis: {
            type: 'value',
            axisLabel: {
              formatter: (value) => this.formatAxisCurrency(value)
            }
          },
          series: series
        }
      }

      const series = this.trendsData.series.map((merchantData, index) => ({
        name: merchantData.merchant_name,
        type: 'line',
        smooth: true,
        stack: 'total',
        areaStyle: {},
        data: merchantData.bank_data,
        itemStyle: {
          color: this.getColorForIndex(index)
        }
      }))

      return {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'cross'
          },
          formatter: (params) => {
            let result = `${params[0].axisValue}<br/>`
            let total = 0
            params.reverse().forEach(param => {
              result += `${param.marker} ${param.seriesName}: ${this.formatCurrency(param.value)}<br/>`
              total += param.value
            })
            result += `<strong>Total: ${this.formatCurrency(total)}</strong>`
            return result
          }
        },
        toolbox: {
          feature: {
            saveAsImage: { title: 'Export PNG' },
            dataZoom: { title: { zoom: 'Zoom', back: 'Reset' } },
            restore: { title: 'Restore' }
          }
        },
        legend: {
          data: this.trendsData.series.map(s => s.merchant_name),
          bottom: 0,
          type: 'scroll'
        },
        dataZoom: [
          { type: 'inside' },
          { type: 'slider', height: 20, bottom: 60 }
        ],
        grid: {
          left: '3%',
          right: '4%',
          bottom: '20%',
          top: '10%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: this.trendsData.dates,
          axisLabel: {
            rotate: 45,
            formatter: (value) => {
              const date = new Date(value)
              return `${date.getMonth() + 1}/${date.getDate()}`
            }
          }
        },
        yAxis: {
          type: 'value',
          axisLabel: {
            formatter: (value) => this.formatAxisCurrency(value)
          }
        },
        series: series
      }
    }
  },

  watch: {
    selectedMerchantKsuids () {
      this.debouncedFetchTrendsData()
    },

    startDate () {
      this.debouncedFetchTrendsData()
    },

    endDate () {
      this.debouncedFetchTrendsData()
    }
  },

  mounted () {
    this.fetchTrendsData()
  },

  beforeDestroy () {
    if (this.fetchDebounceTimer) {
      clearTimeout(this.fetchDebounceTimer)
    }
  },

  methods: {
    debouncedFetchTrendsData () {
      if (this.fetchDebounceTimer) {
        clearTimeout(this.fetchDebounceTimer)
      }

      this.fetchDebounceTimer = setTimeout(() => {
        this.fetchTrendsData()
      }, 300)
    },

    async fetchTrendsData () {
      this.internalLoading = true
      try {
        let merchantKsuids = []

        if (this.selectedMerchantKsuids.length > 0) {
          merchantKsuids = this.selectedMerchantKsuids
        }

        const response = await this.getTrendsData(this.startDate, this.endDate, merchantKsuids)
        const data = response.data.data

        if (data && data.series && data.series.length > 0) {
          this.trendsData = {
            dates: data.dates || [],
            series: data.series
          }
        }
      } catch (error) {
        console.error('Error fetching trends data:', error)
      } finally {
        this.internalLoading = false
      }
    },

    formatCurrency (value) {
      if (typeof value === 'string') {
        value = parseFloat(value)
      }
      if (isNaN(value)) {
        return '$0.00'
      }
      return value.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    },

    formatAxisCurrency (value) {
      if (value >= 1000000) {
        return '$' + (value / 1000000).toFixed(1) + 'M'
      } else if (value >= 1000) {
        return '$' + (value / 1000).toFixed(0) + 'K'
      }
      return '$' + value.toFixed(0)
    },

    getColorForIndex (index) {
      const colors = [
        '#1976D2',
        '#388E3C',
        '#F57C00',
        '#7B1FA2',
        '#C2185B',
        '#0097A7',
        '#5D4037',
        '#455A64'
      ]
      return colors[index % colors.length]
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/manage/paynetworx/funding/TrendsView.vue